'use client';

import { useTranslation } from 'react-i18next';
import { CloudArrowUpIcon } from '@heroicons/react/24/outline';
import { FILE_FORMAT_LABELS, MAX_FILE_SIZE_MB } from '../screening.constants';

interface FileUploadZoneProps {
  isDragActive: boolean;
  onDragEnter: (e: React.DragEvent) => void;
  onDragLeave: (e: React.DragEvent) => void;
  onDragOver: (e: React.DragEvent) => void;
  onDrop: (e: React.DragEvent) => void;
  onFileInput: (e: React.ChangeEvent<HTMLInputElement>) => void;
  compact?: boolean;
}

export const FileUploadZone = ({
  isDragActive,
  onDragEnter,
  onDragLeave,
  onDragOver,
  onDrop,
  onFileInput,
  compact = false,
}: FileUploadZoneProps) => {
  const { t } = useTranslation();

  if (compact) {
    return (
      <div className="mb-4 flex items-center justify-between rounded-xl border border-dashed border-slate-200 bg-slate-50/50 p-4">
        <span className="text-sm font-medium text-slate-600">
          {t('screening.add.more')}
        </span>
        <label htmlFor="file-upload-compact">
          <span className="inline-flex cursor-pointer items-center justify-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-sm font-semibold text-slate-700 transition-colors hover:bg-slate-50">
            {t('screening.upload.more')}
          </span>
          <input
            id="file-upload-compact"
            type="file"
            multiple
            accept=".pdf,.doc,.docx,.txt,.csv,.xls,.xlsx"
            onChange={onFileInput}
            className="sr-only"
          />
        </label>
      </div>
    );
  }

  return (
    <div
      onDragEnter={onDragEnter}
      onDragLeave={onDragLeave}
      onDragOver={onDragOver}
      onDrop={onDrop}
      className={`relative mb-6 flex flex-col items-center justify-center rounded-2xl border-2 border-dashed p-10 transition-all duration-200 ${
        isDragActive
          ? 'border-secondary bg-gradient-to-br from-secondary/5 to-secondary/10'
          : 'border-slate-200 bg-slate-50/50 hover:border-secondary/50 hover:bg-secondary/5'
      }`}
    >
      <div
        className={`flex size-20 items-center justify-center rounded-2xl transition-all ${
          isDragActive
            ? 'scale-110 bg-gradient-to-br from-secondary to-secondary/80 shadow-lg'
            : 'bg-gradient-to-br from-slate-200 to-slate-300'
        }`}
      >
        <CloudArrowUpIcon
          className={`size-10 transition-colors ${
            isDragActive ? 'text-white' : 'text-slate-500'
          }`}
        />
      </div>
      <p className="mt-4 text-lg font-semibold text-slate-700 dark:text-slate-200">
        {isDragActive ? t('screening.drop.cvs') : t('screening.drop.cvs.to.upload')}
      </p>
      <label htmlFor="file-upload" className="mt-4 cursor-pointer">
        <span className="inline-flex items-center justify-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-2 font-semibold text-slate-700 transition-colors hover:bg-slate-50">
          {t('screening.browse')}
        </span>
        <input
          id="file-upload"
          type="file"
          multiple
          accept=".pdf,.doc,.docx,.csv,.xls,.xlsx"
          onChange={onFileInput}
          className="sr-only"
        />
      </label>
      <div className="mt-4 flex items-center gap-2">
        {FILE_FORMAT_LABELS.map((format) => (
          <span
            key={format}
            className="rounded-md bg-slate-200 px-2.5 py-1 text-xs font-medium text-slate-600"
          >
            {format}
          </span>
        ))}
        <span className="text-xs text-slate-400 dark:text-slate-300">
          {t('screening.up.to')}
          {' '}
          {MAX_FILE_SIZE_MB}
          {t('screening.mb.each')}
        </span>
      </div>
    </div>
  );
};

export default FileUploadZone;
